import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, Fingerprint, Smartphone, CheckCircle, Loader2, AlertTriangle, ArrowRight, ArrowLeft, Lock, Globe, Building2, ShieldCheck, UserCog, Search, MapPin, Monitor, Radio } from 'lucide-react';
import { Button } from '../components/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { toast } from 'sonner';
import { DUMMY_ACCOUNTS } from '../data/rbac';
import type { BoxIcon } from 'lucide-react';
// ---------------------------------------------------------------------------
// Demo role definitions — one-click, no credentials. Maps each role to its
// landing route and the access summary stakeholders see on the card.
// ---------------------------------------------------------------------------
interface DemoRole {
  role: string;
  label: string;
  region: string;
  icon: BoxIcon;
  access: string[];
  buttonLabel: string;
  route: string;
  accent: string;
}
const DEMO_ROLES: DemoRole[] = [{
  role: 'Super Admin Nasional',
  label: 'Super Admin Nasional',
  region: 'Seluruh Indonesia',
  icon: Globe,
  access: ['Seluruh Indonesia', 'Seluruh BINDA', 'Dashboard Nasional', 'KPI Nasional', 'Master Data'],
  buttonLabel: 'Masuk sebagai Super Admin',
  route: '/',
  accent: 'var(--primary)'
}, {
  role: 'Administrator Nasional',
  label: 'Administrator Nasional',
  region: 'Nasional',
  icon: UserCog,
  access: ['Monitoring Nasional', 'Laporan Nasional', 'Monitoring BINDA'],
  buttonLabel: 'Masuk sebagai Administrator',
  route: '/',
  accent: 'var(--primary)'
}, {
  role: 'Kepala BINDA',
  label: 'Kepala BINDA Sumatera Utara',
  region: 'Sumatera Utara',
  icon: Building2,
  access: ['Dashboard BINDA', 'KPI Wilayah', 'Monitoring Personel'],
  buttonLabel: 'Masuk sebagai Kepala BINDA',
  route: '/',
  accent: 'var(--status-info)'
}, {
  role: 'Admin BINDA',
  label: 'Admin BINDA Riau',
  region: 'Riau',
  icon: ShieldCheck,
  access: ['Kelola Personel', 'Verifikasi Laporan', 'KPI Wilayah'],
  buttonLabel: 'Masuk sebagai Admin BINDA',
  route: '/',
  accent: 'var(--status-info)'
}, {
  role: 'Analis',
  label: 'Analis Nasional',
  region: 'Nasional',
  icon: Search,
  access: ['Verifikasi', 'Analisis', 'Eskalasi'],
  buttonLabel: 'Masuk sebagai Analis',
  route: '/',
  accent: 'var(--status-standby)'
}, {
  role: 'Koordinator Wilayah',
  label: 'Koordinator Wilayah',
  region: 'Jawa Barat',
  icon: MapPin,
  access: ['Monitoring Wilayah', 'Monitoring Personel'],
  buttonLabel: 'Masuk sebagai Koordinator',
  route: '/',
  accent: 'var(--status-standby)'
}, {
  role: 'Operator Command Center',
  label: 'Operator Command Center',
  region: 'Pusat Komando Nasional',
  icon: Monitor,
  access: ['Command Center', 'Alert Center', 'Incident Feed'],
  buttonLabel: 'Masuk Command Center',
  route: '/command-center',
  accent: 'var(--primary)'
}, {
  role: 'Personel Lapangan',
  label: 'Personel Lapangan',
  region: 'Mobile App Demo',
  icon: Radio,
  access: ['Buat Laporan', 'KPI Pribadi', 'Panic Button'],
  buttonLabel: 'Masuk Aplikasi Mobile',
  route: '/mobile',
  accent: 'var(--status-active)'
}];
export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<'demo' | 'enterprise'>('demo');
  // --- enterprise credential flow state ---
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [locked, setLocked] = useState(false);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('timeout') === 'true') {
      toast.error('Sesi Anda telah berakhir. Silakan login kembali.');
    }
  }, [location]);
  // One-click demo login — no credentials, no MFA.
  const handleDemoLogin = (demo: DemoRole) => {
    localStorage.setItem('bimcs_role', demo.role);
    localStorage.setItem('bimcs_authed', 'true');
    try {
      window.dispatchEvent(new Event('storage'));
    } catch {}
    toast.success(`Masuk sebagai ${demo.label}`);
    navigate(demo.route);
  };
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (locked) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      const account = DUMMY_ACCOUNTS.find(a => a.username === username);
      if (!account || password !== 'password123') {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        if (newAttempts >= 3) {
          setLocked(true);
          toast.error('Akun terkunci karena terlalu banyak percobaan gagal.');
        } else {
          toast.error(`Kredensial tidak valid. Sisa percobaan: ${3 - newAttempts}`);
        }
        return;
      }
      setAvailableRoles(account.roles);
      setStep(2);
      toast.success('Kredensial diverifikasi. Menunggu MFA.');
    }, 1000);
  };
  const handleMFA = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      if (otp !== '123456') {
        toast.error('Kode OTP tidak valid.');
        return;
      }
      setStep(3);
      toast.success('MFA diverifikasi.');
    }, 1000);
  };
  const handleRoleSelect = (role: string) => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      localStorage.setItem('bimcs_role', role);
      setStep(4);
      toast.success(`Peran dipilih: ${role}`);
    }, 800);
  };
  const handleDeviceBind = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      localStorage.setItem('bimcs_authed', 'true');
      toast.success('Perangkat berhasil ditautkan.');
      navigate('/');
    }, 1500);
  };
  const resetEnterprise = () => {
    setStep(1);
    setUsername('');
    setPassword('');
    setOtp('');
    setAttempts(0);
    setLocked(false);
    setAvailableRoles([]);
  };
  return <div className="min-h-screen bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] bg-repeat bg-background flex items-center justify-center p-4 relative" data-id="element-1806">
      <div className="absolute inset-0 bg-gradient-to-b from-background/80 to-background pointer-events-none -z-10" data-id="element-1807"></div>

      {mode === 'demo' ? <div className="w-full max-w-6xl animate-in fade-in duration-500 py-8" data-id="element-1808">
          {/* Top Section */}
          <div className="text-center mb-10" data-id="element-1809">
            <div className="w-20 h-20 mx-auto flex items-center justify-center mb-4" data-id="element-1810">
              <img src="https://cdn.magicpatterns.com/uploads/hos8i69ZV8fMbtkJgKVvED/Logo_BIN_NEW.png" alt="Logo Badan Intelijen Negara Republik Indonesia" className="w-full h-full object-contain drop-shadow-[0_0_20px_rgba(var(--primary),0.25)]" data-id="element-1811" />
            </div>
            <h1 className="text-3xl font-heading font-bold tracking-widest text-primary text-glow-primary" data-id="element-1812">
              DENS CAKRA
            </h1>
            <p className="text-[11px] uppercase tracking-widest text-foreground/90 font-mono mt-2 max-w-md leading-relaxed" data-id="element-1813">
              Dashboard Evaluasi Nasional dan Situational Awareness - Command,
              Analytic, Knowledge, Response &amp; Awareness
            </p>
            <p className="text-[11px] italic tracking-wider text-primary/80 font-mono mt-3" data-id="element-1814">
              "Velox Et Exactus"
            </p>
          </div>

          {/* Section: Masuk Sebagai */}
          <div className="flex items-center justify-between mb-5 border-b border-border/50 pb-3" data-id="element-1815">
            <div className="flex items-center gap-3" data-id="element-1816">
              <span className="text-sm font-mono uppercase tracking-widest text-foreground" data-id="element-1817">
                Masuk Sebagai
              </span>
              <span className="text-[10px] font-mono text-muted-foreground bg-primary/10 border border-primary/30 text-primary px-2 py-0.5 rounded" data-id="element-1818">
                MODE DEMO
              </span>
            </div>
            <button onClick={() => {
          resetEnterprise();
          setMode('enterprise');
        }} className="flex items-center gap-2 text-[11px] font-mono text-muted-foreground hover:text-primary transition-colors border border-border/50 hover:border-primary/50 rounded px-3 py-1.5" data-id="element-1819">
              <Lock className="w-3 h-3" data-id="element-1820" />
              Login Enterprise
            </button>
          </div>

          {/* Role Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1821">
            {DEMO_ROLES.map(demo => {
          const Icon = demo.icon;
          return <Card key={demo.label} className="glass-panel border-border/50 flex flex-col hover:border-primary/50 transition-colors group" data-id="element-1822">
                  <CardContent className="p-4 flex flex-col flex-1" data-id="element-1823">
                    <div className="flex items-center gap-3 mb-3" data-id="element-1824">
                      <div className="w-11 h-11 rounded-full flex items-center justify-center border shrink-0" style={{
                  backgroundColor: `color-mix(in srgb, ${demo.accent} 12%, transparent)`,
                  borderColor: `color-mix(in srgb, ${demo.accent} 40%, transparent)`
                }} data-id="element-1825">
                        <Icon className="w-5 h-5" style={{
                    color: demo.accent
                  }} data-id="element-1826" />
                      </div>
                      <div className="min-w-0" data-id="element-1827">
                        <h3 className="text-sm font-bold leading-tight" data-id="element-1828">
                          {demo.label}
                        </h3>
                        <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mt-0.5 truncate" data-id="element-1829">
                          {demo.region}
                        </p>
                      </div>
                    </div>

                    <div className="flex-1" data-id="element-1830">
                      <p className="text-[9px] font-mono text-muted-foreground uppercase tracking-widest mb-2" data-id="element-1831">
                        Hak Akses
                      </p>
                      <ul className="space-y-1 mb-4" data-id="element-1832">
                        {demo.access.map(a => <li key={a} className="flex items-center gap-2 text-[11px] text-foreground/80" data-id="element-1833">
                            <CheckCircle className="w-3 h-3 shrink-0" style={{
                      color: demo.accent
                    }} data-id="element-1834" />
                            {a}
                          </li>)}
                      </ul>
                    </div>

                    <Button onClick={() => handleDemoLogin(demo)} className="w-full bg-primary/10 text-primary border border-primary/30 hover:bg-primary hover:text-primary-foreground font-mono text-[10px] tracking-widest uppercase transition-colors" data-id="element-1835">
                      {demo.buttonLabel}
                      <ArrowRight className="w-3 h-3 ml-2 group-hover:translate-x-0.5 transition-transform" data-id="element-1836" />
                    </Button>
                  </CardContent>
                </Card>;
        })}
          </div>

          <div className="text-center mt-8 space-y-1" data-id="element-1837">
            <p className="text-[10px] font-mono text-muted-foreground" data-id="element-1838">
              Simulasi prototipe // Satu klik untuk menjelajah setiap peran
              tanpa kredensial.
            </p>
            <p className="text-[10px] font-mono uppercase tracking-widest text-primary/70" data-id="element-1839">
              National Intelligence Monitoring Platform
            </p>
          </div>
        </div> : <Card className="w-full max-w-md glass-panel-heavy border-primary/30 shadow-[0_0_30px_rgba(var(--primary),0.1)] animate-in fade-in slide-in-from-bottom-4" data-id="element-1840">
          <CardHeader className="text-center space-y-4 pb-6 border-b border-border/50 relative" data-id="element-1841">
            <button onClick={() => {
          resetEnterprise();
          setMode('demo');
        }} className="absolute left-0 top-0 flex items-center gap-1 text-[10px] font-mono text-muted-foreground hover:text-primary transition-colors" data-id="element-1842">
              <ArrowLeft className="w-3 h-3" data-id="element-1843" />
              Mode Demo
            </button>
            <div className="w-20 h-20 mx-auto flex items-center justify-center" data-id="element-1844">
              <img src="https://cdn.magicpatterns.com/uploads/hos8i69ZV8fMbtkJgKVvED/Logo_BIN_NEW.png" alt="Logo Badan Intelijen Negara Republik Indonesia" className="w-full h-full object-contain drop-shadow-[0_0_20px_rgba(var(--primary),0.25)]" data-id="element-1845" />
            </div>
            <div data-id="element-1846">
              <CardTitle className="text-2xl font-heading font-bold tracking-widest text-primary text-glow-primary" data-id="element-1847">
                DENS CAKRA ENTERPRISE
              </CardTitle>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono mt-2" data-id="element-1848">
                Otentikasi Aman // Hanya Demonstrasi
              </p>
            </div>
          </CardHeader>

          <CardContent className="p-6" data-id="element-1849">
            {step === 1 && <form onSubmit={handleLogin} className="space-y-4 animate-in fade-in slide-in-from-bottom-4" data-id="element-1850">
                <div className="space-y-2" data-id="element-1851">
                  <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-1852">
                    ID Operatif
                  </label>
                  <input type="text" required value={username} onChange={e => setUsername(e.target.value)} className="w-full bg-black/40 border border-border/50 rounded p-2.5 text-sm font-mono focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50" placeholder="Masukkan ID..." data-id="element-1853" />
                </div>
                <div className="space-y-2" data-id="element-1854">
                  <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground" data-id="element-1855">
                    Kata Sandi
                  </label>
                  <input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-black/40 border border-border/50 rounded p-2.5 text-sm font-mono focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50" placeholder="••••••••" disabled={locked} data-id="element-1856" />
                  <p className="text-[9px] text-muted-foreground font-mono mt-1" data-id="element-1857">
                    Gunakan sandi: password123
                  </p>
                </div>

                {locked && <div className="bg-destructive/10 border border-destructive/30 p-3 rounded flex items-start gap-2 mt-4" data-id="element-1858">
                    <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" data-id="element-1859" />
                    <p className="text-[10px] font-mono text-destructive" data-id="element-1860">
                      AKUN TERKUNCI. Silakan hubungi Administrator Sistem atau
                      tunggu 15 menit.
                    </p>
                  </div>}

                <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-mono text-xs tracking-widest uppercase mt-4" disabled={loading || locked} data-id="element-1861">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-1862" /> : locked ? 'Terkunci' : 'Otentikasi'}
                </Button>
              </form>}

            {step === 2 && <form onSubmit={handleMFA} className="space-y-6 animate-in fade-in slide-in-from-bottom-4" data-id="element-1863">
                <div className="text-center space-y-2" data-id="element-1864">
                  <Fingerprint className="w-12 h-12 text-primary mx-auto opacity-80" data-id="element-1865" />
                  <p className="text-xs font-mono text-muted-foreground" data-id="element-1866">
                    Diperlukan Verifikasi Biometrik atau OTP
                  </p>
                </div>
                <div className="space-y-2" data-id="element-1867">
                  <input type="text" required maxLength={6} value={otp} onChange={e => setOtp(e.target.value)} className="w-full bg-black/40 border border-border/50 rounded p-3 text-center text-2xl tracking-[1em] font-mono focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50" placeholder="------" data-id="element-1868" />
                  <p className="text-[9px] text-muted-foreground font-mono mt-1 text-center" data-id="element-1869">
                    Gunakan OTP: 123456
                  </p>
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-mono text-xs tracking-widest uppercase" disabled={loading || otp.length !== 6} data-id="element-1870">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-1871" /> : 'Verifikasi Identitas'}
                </Button>
              </form>}

            {step === 3 && <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4" data-id="element-1872">
                <p className="text-xs font-mono text-muted-foreground text-center mb-4" data-id="element-1873">
                  Pilih Peran Operasional
                </p>
                <div className="grid grid-cols-1 gap-2" data-id="element-1874">
                  {availableRoles.map(role => <Button key={role} variant="outline" onClick={() => handleRoleSelect(role)} disabled={loading} className="justify-start font-mono text-xs border-border/50 hover:bg-primary/10 hover:border-primary/50 hover:text-primary transition-all" data-id="element-1875">
                      <Shield className="w-3 h-3 mr-3 opacity-50" data-id="element-1876" />
                      {role}
                    </Button>)}
                </div>
              </div>}

            {step === 4 && <div className="space-y-6 text-center animate-in fade-in slide-in-from-bottom-4" data-id="element-1877">
                <div className="w-20 h-20 mx-auto bg-black/40 border border-border/50 rounded-full flex items-center justify-center relative" data-id="element-1878">
                  <Smartphone className="w-8 h-8 text-primary" data-id="element-1879" />
                  <div className="absolute inset-0 border-2 border-primary rounded-full animate-ping opacity-20" data-id="element-1880"></div>
                </div>
                <div data-id="element-1881">
                  <h3 className="font-mono text-sm text-foreground mb-1" data-id="element-1882">
                    Penautan Perangkat Diperlukan
                  </h3>
                  <p className="text-xs text-muted-foreground" data-id="element-1883">
                    Daftarkan terminal ini untuk akses aman
                  </p>
                </div>
                <div className="bg-black/40 border border-border/50 rounded p-4" data-id="element-1884">
                  <p className="text-[10px] font-mono text-muted-foreground break-all" data-id="element-1885">
                    SIDIK JARI: 8F9A-2B3C-4D5E-6F7A-8B9C-0D1E-2F3A-4B5C
                  </p>
                </div>
                <Button onClick={handleDeviceBind} className="w-full bg-[var(--status-active)] hover:bg-[var(--status-active)]/80 text-black font-mono text-xs tracking-widest uppercase" disabled={loading} data-id="element-1886">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-1887" /> : <>
                      <CheckCircle className="w-4 h-4 mr-2" data-id="element-1888" />
                      Konfirmasi Penautan
                    </>}
                </Button>
              </div>}
          </CardContent>
        </Card>}
    </div>;
}